import { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Select,
  Space,
  Button,
  AutoComplete,
  Modal,
  notification,
} from 'antd';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import axios from 'axios';

import { auth } from '../firebase';
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from 'firebase/auth';

import { getServiceUrl } from '../config';
import { useNavigate } from 'react-router-dom';

const SignupRevamp = () => {
  const [form] = Form.useForm();
  const [hospitalData, setHospitalData] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const navigate = useNavigate();

  const BASE_URL = getServiceUrl();
  const formValidationDebounce = 300;

  const submitHandler = async (formData) => {
    setSubmitting(true);
    const hospitalId = (() => {
      const hospital = hospitalData.filter(
        (x) => x.value.toLowerCase() === formData.institution.toLowerCase(),
      );
      return hospital[0]?.hospital_id;
    })();

    if (hospitalId === undefined) {
      notification.info({
        message: 'Invalid Hospital',
        description: 'Please select a valid hospital.',
      });
      setSubmitting(false);
      return;
    }
    delete formData.institution;
    formData.hospitalId = hospitalId;

    try {
      await axios.post(BASE_URL + 'doctors/newuser', formData);
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        form.getFieldValue('email'),
        form.getFieldValue('password'),
      );
      // Send verification email
      await sendEmailVerification(userCredential.user, {
        // Optionally set a redirect URL after email verification
        url: 'http://localhost:3000/login',
      });
      Modal.success({
        title: 'Account Created',
        content:
          'Acount created successfully. Please check your email for verification.',
        afterClose: () => navigate('/login'),
        centered: true,
        closable: true,
        animation: true,
      });
      setSubmitting(false);
    } catch (error) {
      if (error.response.status === 400) {
        notification.info({
          message: 'User Exists',
          description:
            'This email address is already registered. Please use a different email address.',
        });
      } else {
        notification.error({
          message: 'Error',
          description: 'An error occurred while creating your account.',
        });
      }
      console.log(error);
      setSubmitting(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const hospitals = await axios.get(BASE_URL + 'hospital/getallhospital');
        setHospitalData(hospitals.data);
      } catch (error) {
        console.log(error);
        notification.error({
          message: 'Error',
          description: 'An error occurred while fetching hospitals.',
        });
      }
    })();
  }, []);

  return (
    <div className="flex">
      <div className="flex h-screen w-1/2 flex-col items-center justify-center bg-slate-50 px-36">
        <img
          src="/images/rdxlogo.png"
          alt="logo"
          className="mb-4 h-20 object-contain"
        />
        <Form
          form={form}
          className="w-full"
          layout="vertical"
          onFinish={submitHandler}
          size="middle"
        >
          <Space.Compact className="w-full">
            <Form.Item
              hasFeedback
              name="salutation"
              label="Salutation"
              validateDebounce={formValidationDebounce}
              className="w-1/3"
              rules={[
                {
                  required: true,
                  message: 'Salutation required',
                },
              ]}
            >
              <Select
                placeholder="Dr."
                options={[
                  { value: 'Mr.' },
                  { value: 'Mrs.' },
                  { value: 'Miss' },
                  { value: 'Dr.' },
                  { value: 'Prof.' },
                ]}
              />
            </Form.Item>
            <Form.Item
              hasFeedback
              name="firstname"
              label="First Name"
              validateDebounce={formValidationDebounce}
              rules={[
                {
                  required: true,
                  message: 'First name required',
                },
              ]}
            >
              <Input placeholder="First Name" />
            </Form.Item>
            <Form.Item
              hasFeedback
              label="Last Name"
              name="lastname"
              validateDebounce={formValidationDebounce}
              rules={[
                {
                  required: true,
                  message: 'Last name required',
                },
              ]}
            >
              <Input placeholder="Last Name" />
            </Form.Item>
          </Space.Compact>

          <Form.Item
            hasFeedback
            label="Email"
            name="email"
            validateDebounce={1000}
            rules={[
              {
                required: true,
                pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                message: 'Please provide a valid email address.',
              },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="Email" />
          </Form.Item>
          <Form.Item
            hasFeedback
            label="Institution/Organization"
            name="institution"
            validateDebounce={formValidationDebounce}
            rules={[
              {
                required: true,
                message: 'Please provide the name of your institution.',
              },
            ]}
          >
            <AutoComplete
              name="institution"
              options={hospitalData}
              placeholder="Institution Name..."
              filterOption={(inputValue, option) =>
                option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !==
                -1
              }
            />
          </Form.Item>
          <Form.Item
            label="Password"
            hasFeedback
            name="password"
            validateDebounce={formValidationDebounce}
            rules={[
              {
                min: 8,
                required: true,
                message: 'Password must be at least 8 characters long.',
              },
            ]}
          >
            <Space direction="horizontal">
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Password"
                visibilityToggle={{
                  visible: passwordVisible,
                  onVisibleChange: setPasswordVisible,
                }}
              />
              <Button
                onClick={() => setPasswordVisible((prevState) => !prevState)}
              >
                {passwordVisible ? 'Hide' : 'Show'}
              </Button>
            </Space>
          </Form.Item>
          <Form.Item
            hasFeedback
            label="Confirm Password"
            name="confirmPassword"
            validateDebounce={formValidationDebounce}
            rules={[
              {
                min: 8,
                required: true,
                validator: (_, value) =>
                  value.length > 8 && value !== form.getFieldValue('password')
                    ? Promise.reject()
                    : Promise.resolve(),
                message: 'Passwords do not match.',
              },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Confirm Password"
            />
          </Form.Item>
          <Form.Item>
            <Button
              className="w-full"
              type="primary"
              htmlType="submit"
              loading={submitting}
            >
              Create Account
            </Button>
          </Form.Item>
        </Form>
      </div>

      <div
        className="flex h-screen w-1/2 flex-col items-center justify-center"
        style={{
          backgroundImage:
            'url(https://res.cloudinary.com/dxcdxkino/image/upload/v1740814181/digitale-de-2GEr4fLZt8A-unsplash_avmbts.jpg)',
          backgroundSize: '200%',
          backgroundPosition: '60% 75%',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <h1 className="mb-8 text-5xl font-bold text-gray-200">
          <p className="text-center">Your Partner In</p>
          <p className="text-center text-yellow-500">Rare Disease Diagnosis!</p>
        </h1>
        <p className="px-20 text-center text-lg text-gray-200">
          We provide cutting-edge technology and expertise to assist in
          diagnosing rare diseases with precision and care. Trust us to be your
          guide on the journey to better health.
        </p>
        <img
          src="/images/logo.png"
          alt="logo"
          className="m-3 h-6 object-contain"
        />
      </div>
    </div>
  );
};

export default SignupRevamp;
